import React, { useEffect, useState } from 'react';
import {
  jiraAnalyzerReportsAPI,
  JiraMappingConflictError,
} from '@Lib/axios/jiraAnalyzer';
import { JiraMappings, JiraProject } from '@Models/types';
import { jiraAPI } from '@Lib/axios/jira';
import { Trash, X, MagnifyingGlass } from '@phosphor-icons/react';
import { MoonLoader } from 'react-spinners';
import Modal from '@Common/Modal';
import showToast from '@Common/toast/showToast';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { useAuth } from '@Context/CognitoAuthProvider';

const JiraAnalyzerReportsSettings = () => {
  const [mappings, setMappings] = useState<JiraMappings[]>([]);
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [form, setForm] = useState<{ username: string; projectKeys: string[] }>(
    {
      username: '',
      projectKeys: [],
    }
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    username: string;
    projectKey?: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { modals, setModal, loaders, setLoader } = useUtilityContext();
  const { logout } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoader('fetchData', true);
      try {
        const jiraMappings = jiraAnalyzerReportsAPI(logout);
        const jira = jiraAPI(logout);
        const [mappingsRes, projectsRes] = await Promise.all([
          jiraMappings.getMappings(),
          jira.getProjects(),
        ]);
        setMappings(mappingsRes);
        setProjects(projectsRes);
      } catch (err) {
        setError('Failed to load data.');
      }
      setLoader('fetchData', false);
    };
    fetchData();
  }, []);

  const getMappingProjects = (mapping?: JiraMappings) =>
    mapping?.projects ?? [];

  const assignedProjectKeys = mappings.flatMap((m) =>
    getMappingProjects(m).map((p) => p.key)
  );

  const filteredMappings = searchTerm
    ? mappings.filter(
        (mapping) =>
          mapping.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getMappingProjects(mapping).some(
            (project) =>
              project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              project.key.toLowerCase().includes(searchTerm.toLowerCase())
          )
      )
    : mappings;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProjectSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    setForm({ ...form, projectKeys: selected });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoader('submitMapping', true);

    const selectedProjects = projects
      .filter((p) => form.projectKeys.includes(p.key))
      .map((p) => ({
        id: p.id,
        key: p.key,
        name: p.name,
      }));

    try {
      const jiraMappings = jiraAnalyzerReportsAPI(logout);

      const existingMapping = mappings.find(
        (m) => m.username === form.username
      );

      if (existingMapping) {
        const existingProjects = getMappingProjects(existingMapping);
        const existingProjectKeys = existingProjects.map((p) => p.key);
        const newProjects = selectedProjects.filter(
          (project) => !existingProjectKeys.includes(project.key)
        );

        const mergedProjects = [...existingProjects, ...newProjects];

        const result = await jiraMappings.updateMappings({
          username: form.username,
          projects: mergedProjects,
        });

        if (
          result &&
          (result as JiraMappingConflictError).type === 'conflict'
        ) {
          const conflict = result as JiraMappingConflictError;
          const details = conflict.conflicts
            .map(
              (c) =>
                `${c.overlappingProjects.map((p) => p.key).join(', ')} → ${c.username}`
            )
            .join('; ');
          showToast.error(`Conflict: ${details}`);
          return;
        }

        showToast.success('Projects added to existing assignments!');
      } else {
        const result = await jiraMappings.updateMappings({
          username: form.username,
          projects: selectedProjects,
        });

        if (
          result &&
          (result as JiraMappingConflictError).type === 'conflict'
        ) {
          const conflict = result as JiraMappingConflictError;
          const details = conflict.conflicts
            .map(
              (c) =>
                `${c.overlappingProjects.map((p) => p.key).join(', ')} → ${c.username}`
            )
            .join('; ');
          showToast.error(`Conflict: ${details}`);
          return;
        }

        showToast.success('Mapping created successfully!');
      }

      const updated = await jiraMappings.getMappings();
      setMappings(updated);
      setForm({ username: '', projectKeys: [] });
    } catch (err) {
      showToast.error('Failed to update mapping.');
    } finally {
      setLoader('submitMapping', false);
    }
  };

  const handleDeleteMapping = async (username: string) => {
    setDeleteTarget({ username });
    setModal('deleteMapping', true);
  };

  const confirmDeleteMapping = async () => {
    if (!deleteTarget) return;

    setLoader('deleteMapping', true);
    try {
      const jiraMappings = jiraAnalyzerReportsAPI(logout);
      await jiraMappings.deleteMapping(deleteTarget.username);
      showToast.success('Mapping deleted successfully!');

      const updated = await jiraMappings.getMappings();
      setMappings(updated);
    } catch (err) {
      showToast.error('Failed to delete mapping.');
    } finally {
      setModal('deleteMapping', false);
      setDeleteTarget(null);
      setLoader('deleteMapping', false);
    }
  };

  const handleRemoveProject = async (username: string, projectKey: string) => {
    setDeleteTarget({ username, projectKey });
    setModal('removePod', true);
  };

  const confirmRemoveProject = async () => {
    if (!deleteTarget) return;

    setLoader('removePod', true);
    try {
      const jiraMappings = jiraAnalyzerReportsAPI(logout);
      const currentMapping = mappings.find(
        (m) => m.username === deleteTarget.username
      );

      if (!currentMapping) return;

      const updatedProjects = getMappingProjects(currentMapping).filter(
        (project) => project.key !== deleteTarget.projectKey
      );

      const result = await jiraMappings.updateMappings({
        username: deleteTarget.username,
        projects: updatedProjects,
      });

      if (result && (result as JiraMappingConflictError).type === 'conflict') {
        showToast.error('Unexpected conflict while removing project.');
        return;
      }

      showToast.success('Project assignment removed successfully!');

      const updated = await jiraMappings.getMappings();
      setMappings(updated);
    } catch (err) {
      showToast.error('Failed to remove project assignment.');
    } finally {
      setModal('removePod', false);
      setDeleteTarget(null);
      setLoader('removePod', false);
    }
  };

  const getProjectName = (projectKey?: string) => {
    if (!projectKey) return '';
    const project = projects.find((p) => p.key === projectKey);
    return project ? `${project.name} (${project.key})` : projectKey;
  };

  return (
    <div className="flex h-full w-full flex-col gap-3 p-2">
      <div className="flex flex-col">
        <h2 className="text-xl font-semibold text-blue-900">
          Manage Report Assignments
        </h2>
        <p className="text-sm text-blue-900">
          Here you can assign users who will evaluate the reports. These
          settings are associated to Analyzer Reports auditing.
        </p>
      </div>

      <div className="flex h-full w-full flex-row gap-2">
        {/* FORM */}
        <div className="flex h-full w-1/3 flex-col rounded-md border border-gray-400 bg-white p-4">
          <span className="font-semibold">Add/Update Assignments</span>

          <form
            onSubmit={handleSubmit}
            className="flex h-full flex-col gap-y-2"
          >
            <div>
              <label className="text-sm font-medium text-gray-700">
                Username (email)
              </label>
              <input
                type="text"
                name="username"
                placeholder="Enter username or email"
                value={form.username}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
                disabled={loaders.submitMapping}
              />
            </div>

            <div className="flex h-full flex-col">
              <label className="text-sm font-medium text-gray-700">
                Select Projects (Hold Ctrl/Cmd to select multiple)
              </label>
              <select
                multiple
                name="projects"
                value={form.projectKeys}
                onChange={handleProjectSelect}
                className="custom-scroll h-full w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                required
                disabled={loaders.submitMapping}
              >
                {projects.map((project) => {
                  const isAssigned =
                    assignedProjectKeys.includes(project.key) &&
                    !mappings
                      .find((m) => m.username === form.username)
                      ?.projects?.some((p) => p.key === project.key);
                  return (
                    <option
                      key={project.key}
                      value={project.key}
                      disabled={isAssigned}
                      className={isAssigned ? 'text-gray-400' : ''}
                    >
                      {project.name} ({project.key})
                      {isAssigned ? ' (Assigned)' : ''}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500">
                {form.projectKeys.length} project(s) selected
              </p>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="submit"
                className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={loaders.submitMapping}
              >
                {loaders.submitMapping ? (
                  <>
                    <MoonLoader color="#ffffff" size={16} className="mr-2" />
                    Assigning...
                  </>
                ) : (
                  'Assign'
                )}
              </button>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
                {success}
              </div>
            )}
          </form>
        </div>

        {/* LIST */}
        <div className="flex h-full w-2/3 flex-col rounded-md border border-gray-400 bg-white p-4">
          <div className="flex flex-col gap-4">
            <span className="font-semibold">Current Assignments</span>

            {/* Search Input */}
            <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-4 py-2">
              <MagnifyingGlass size={20} weight="bold" />
              <input
                type="text"
                className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
                placeholder="Search assignments by username or project"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <span
                  className="cursor-pointer hover:text-brandRed-500"
                  onClick={() => setSearchTerm('')}
                >
                  <X weight="bold" />
                </span>
              )}
            </div>
          </div>

          {loaders.fetchData ? (
            <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
              <MoonLoader color="#071D49" size={100} />
              <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
                Fetching Assignments
              </span>
            </span>
          ) : filteredMappings.length === 0 ? (
            <div className="p-4 italic">
              {searchTerm
                ? 'No assignments match your search'
                : 'No assignments found. Add your first assignment.'}
            </div>
          ) : (
            <div className="custom-scroll flex h-0 flex-grow flex-col gap-y-2 overflow-auto overflow-y-auto p-2">
              {filteredMappings.map((mapping) => (
                <div
                  key={mapping.username}
                  className="flex w-full flex-row items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-col">
                    <h4 className="font-medium text-gray-900">
                      {mapping.username}
                    </h4>
                    <div className="flex flex-row gap-2">
                      {getMappingProjects(mapping).length === 0 ? (
                        <span className="text-xs italic text-gray-500">
                          No projects assigned
                        </span>
                      ) : (
                        getMappingProjects(mapping).map((project) => (
                          <span
                            key={project.key}
                            className="flex flex-row items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800"
                          >
                            {project.name} ({project.key})
                            <button
                              onClick={() =>
                                handleRemoveProject(
                                  mapping.username,
                                  project.key
                                )
                              }
                              className="ml-1 rounded-full p-0.5 text-blue-600 hover:bg-blue-200 hover:text-blue-800 disabled:opacity-50"
                              title="Remove project assignment"
                              disabled={loaders.removePod}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteMapping(mapping.username)}
                    className="flex rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    title="Remove assignment"
                    disabled={loaders.deleteMapping}
                  >
                    <Trash size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={modals.deleteMapping}
        onClose={() => {
          setModal('deleteMapping', false);
          setDeleteTarget(null);
        }}
        title="Confirm Delete Mapping"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to remove{' '}
            <span className="font-semibold">{deleteTarget?.username}</span> from
            all project assignments?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setModal('deleteMapping', false);
                setDeleteTarget(null);
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={loaders.deleteMapping}
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteMapping}
              className="flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              disabled={loaders.deleteMapping}
            >
              {loaders.deleteMapping ? (
                <>
                  <MoonLoader color="#ffffff" size={16} className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modals.removePod}
        onClose={() => {
          setModal('removePod', false);
          setDeleteTarget(null);
        }}
        title="Confirm Remove Project"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to remove the project{' '}
            <span className="font-semibold">
              {getProjectName(deleteTarget?.projectKey)}
            </span>{' '}
            from <span className="font-semibold">{deleteTarget?.username}</span>
            ?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setModal('removePod', false);
                setDeleteTarget(null);
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={loaders.removePod}
            >
              Cancel
            </button>
            <button
              onClick={confirmRemoveProject}
              className="flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              disabled={loaders.removePod}
            >
              {loaders.removePod ? (
                <>
                  <MoonLoader color="#ffffff" size={16} className="mr-2" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default JiraAnalyzerReportsSettings;
